import type { Texture } from 'pixi.js';
import { PixelBuf, hex, rand } from './pixelfx';

/**
 * 맵 도트 — 사천 땅의 야생. 바닥 타일과 장식(대숲·나무·바위·풀…)을 코드가 찍는다.
 *
 * 이펙트와 같은 규칙을 따른다: 정수 격자, 색 램프, 최근접 확대.
 * 그리고 하나 더 — **어둡고 차분하게.** 적·탄·보석이 늘 바닥보다 밝아야 한눈에 읽힌다.
 * 그래서 바닥은 명도 10% 안팎, 장식의 가장 밝은 점도 30% 를 넘지 않게 잡았다.
 */

type RGB = [number, number, number];
const C = (ramp: string[]): RGB[] => ramp.map(hex);

/** 4×4 베이어 행렬 — 두 색 사이를 도트로 섞을 때 쓴다(반투명 대신) */
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const bayer = (x: number, y: number): number => BAYER[(y & 3) * 4 + (x & 3)] / 16;

/** 이음매 없이 반복되는 값 노이즈 (size 가 cells 로 나눠떨어져야 한다) */
function tileNoise(size: number, cells: number, seed: number): (x: number, y: number) => number {
  const r = rand(seed);
  const g = new Float32Array(cells * cells);
  for (let i = 0; i < g.length; i++) g[i] = r();
  const cs = size / cells;
  const at = (i: number, j: number): number => g[(((j % cells) + cells) % cells) * cells + (((i % cells) + cells) % cells)];
  return (x, y) => {
    const gx = x / cs;
    const gy = y / cs;
    const x0 = Math.floor(gx);
    const y0 = Math.floor(gy);
    const fx = gx - x0;
    const fy = gy - y0;
    const sx = fx * fx * (3 - 2 * fx);
    const sy = fy * fy * (3 - 2 * fy);
    const a = at(x0, y0);
    const b = at(x0 + 1, y0);
    const c = at(x0, y0 + 1);
    const d = at(x0 + 1, y0 + 1);
    return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
  };
}

// ── 바닥 ────────────────────────────────────────────────

/** 이끼 낀 검푸른 흙 (어두움 → 밝음) */
const GROUND = C(['#0f1009', '#13140c', '#17190f', '#1b1e12', '#202416']);
/** 사천의 붉은 흙이 드러난 자리 */
const SOIL = C(['#130e0a', '#18110c', '#1d140e', '#231811', '#2a1d14']);

/**
 * 반복 바닥 타일. 크게 번지는 얼룩(흙·이끼) 위에 풀씨·잔돌을 흩뿌린다.
 * 반복이 눈에 띄지 않게 크게(512) 굽고, 큰 무늬는 따로 놓는 장식이 맡는다.
 */
export function groundTile(size = 512): Texture {
  const buf = new PixelBuf(size, size);
  const n1 = tileNoise(size, 8, 101);
  const n2 = tileNoise(size, 16, 202);
  const n3 = tileNoise(size, 64, 303);
  const soilN = tileNoise(size, 8, 404);
  const soilN2 = tileNoise(size, 32, 505);
  const r = rand(606);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const v = n1(x, y) * 0.55 + n2(x, y) * 0.3 + n3(x, y) * 0.15 + (bayer(x, y) - 0.5) * 0.09;
      const idx = Math.max(0, Math.min(4, Math.floor((v - 0.18) * 7.5)));
      const s = soilN(x, y) * 0.75 + soilN2(x, y) * 0.25 + (bayer(x + 1, y + 2) - 0.5) * 0.06;
      buf.set(x, y, s > 0.64 ? SOIL[idx] : GROUND[idx]);
    }
  }
  // 풀씨·잔돌. 이음매를 넘어가는 점은 반대편에 찍는다(타일이 이어지게)
  const put = (x: number, y: number, c: RGB): void => buf.set(((x % size) + size) % size, ((y % size) + size) % size, c);
  const blade = C(['#1f2915', '#28361b', '#324522']);
  const stone = C(['#2e2f2d', '#3a3b38', '#1a1a18']);
  for (let i = 0; i < size * size * 0.0022; i++) {
    const x = Math.floor(r() * size);
    const y = Math.floor(r() * size);
    const k = r();
    if (k < 0.55) {
      // 가는 풀 한 가닥
      const h = 2 + Math.floor(r() * 3);
      for (let j = 0; j < h; j++) put(x + (j === h - 1 && r() < 0.5 ? 1 : 0), y - j, blade[Math.min(2, j)]);
    } else if (k < 0.85) {
      // 잔돌 — 위가 밝다
      put(x, y, stone[0]);
      put(x + 1, y, stone[2]);
      put(x, y - 1, stone[1]);
    } else {
      put(x, y, blade[2]);
    }
  }
  return buf.toTexture();
}

// ── 장식 공용 ───────────────────────────────────────────

export interface Prop {
  texture: Texture;
  /** 스프라이트 anchor (밑동 기준으로 세우는 것은 아래 가운데) */
  ax: number;
  ay: number;
}

/** 잎이 뭉친 덩이 하나를 찍는다. 왼쪽 위가 밝고 오른쪽 아래가 어둡다. */
function clump(buf: PixelBuf, cx: number, cy: number, rad: number, ramp: RGB[], r: () => number, lightBias = 0): void {
  const R = Math.max(2, rad);
  for (let y = Math.floor(cy - R); y <= Math.ceil(cy + R); y++) {
    for (let x = Math.floor(cx - R); x <= Math.ceil(cx + R); x++) {
      const dx = x - cx;
      const dy = y - cy;
      const d = Math.hypot(dx, dy);
      if (d > R + (r() - 0.5) * 1.2) continue;
      // 빛은 왼쪽 위에서
      const lit = -(dx * 0.6 + dy * 0.8) / R + lightBias;
      let k = 2 - lit * 1.6 + (d / R) * 0.9 + (bayer(x, y) - 0.5) * 0.9;
      if (d > R - 1) k = Math.max(k, 3.4); // 가장자리는 어둡게 = 덩이가 겹쳐도 구분된다
      buf.set(x, y, ramp[Math.max(0, Math.min(ramp.length - 1, Math.round(k)))]);
    }
  }
}

/** 밑동 그림자 — 체크무늬로 반만 찍어 비쳐 보이게 */
function shadow(buf: PixelBuf, cx: number, cy: number, rx: number, ry: number): void {
  const c = hex('#070805');
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      const e = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
      if (e > 1) continue;
      if (e > 0.45 && ((x + y) & 1) === 0) continue;
      buf.set(x, y, c);
    }
  }
}

function prop(buf: PixelBuf, ax = 0.5, ay = 1): Prop {
  return { texture: buf.toTexture(), ax, ay };
}

// ── 대숲 ────────────────────────────────────────────────

const BAMBOO = C(['#6a8f45', '#557636', '#435e2a', '#314620', '#1f2e15']);
const BAMBOO_LEAF = C(['#58803a', '#44672e', '#345223', '#253c19', '#172610']);

/** 대나무 한 무더기. 줄기 5~8대가 살짝 기울어 서고 위쪽에 잎이 늘어진다. */
export function bamboo(seed: number): Prop {
  const r = rand(9100 + seed * 71);
  const w = 72;
  const h = 150;
  const buf = new PixelBuf(w, h);
  const base = h - 5;
  shadow(buf, w / 2, base, 26, 5);
  const n = 5 + Math.floor(r() * 4);
  const stalks: Array<{ x: number; lean: number; top: number; back: boolean }> = [];
  for (let i = 0; i < n; i++) {
    stalks.push({ x: 18 + r() * 36, lean: (r() - 0.5) * 0.22, top: 70 + r() * 70, back: r() < 0.4 });
  }
  // 뒤에 선 줄기부터
  stalks.sort((a, b) => Number(b.back) - Number(a.back));
  const leaves: Array<[number, number, number]> = [];
  for (const s of stalks) {
    const shade = s.back ? 1 : 0; // 뒤쪽 줄기는 한 단 어둡다
    const gap = 11 + Math.floor(r() * 4);
    for (let y = 0; y < s.top; y++) {
      const x = Math.round(s.x + s.lean * y);
      const py = base - y;
      const node = y % gap === 0 && y > 4;
      buf.set(x - 1, py, BAMBOO[Math.min(4, 1 + shade)]);
      buf.set(x, py, BAMBOO[Math.min(4, (node ? 3 : 1) + shade)]);
      buf.set(x + 1, py, BAMBOO[Math.min(4, (node ? 4 : 3) + shade)]);
      if (node && y > s.top * 0.45) leaves.push([x, py, shade]);
    }
    leaves.push([Math.round(s.x + s.lean * s.top), base - s.top, shade]);
  }
  // 잎: 마디에서 비스듬히 아래로 늘어지는 가는 잎
  for (const [x0, y0, shade] of leaves) {
    const k = 2 + Math.floor(r() * 3);
    for (let i = 0; i < k; i++) {
      const dir = r() < 0.5 ? -1 : 1;
      const len = 6 + Math.floor(r() * 7);
      const droop = 0.25 + r() * 0.6;
      for (let j = 1; j <= len; j++) {
        const x = Math.round(x0 + dir * j);
        const y = Math.round(y0 + j * droop + (j > len * 0.6 ? (j - len * 0.6) * 0.5 : 0));
        const c = BAMBOO_LEAF[Math.min(4, (j < 3 ? 2 : j > len - 2 ? 1 : 0) + shade + 1)];
        buf.set(x, y, c);
        if (j > 1 && j < len - 1) buf.set(x, y + 1, BAMBOO_LEAF[Math.min(4, 3 + shade)]);
      }
    }
  }
  return prop(buf);
}

// ── 나무 ────────────────────────────────────────────────

const TRUNK = C(['#5a4030', '#463223', '#34251a', '#231911']);
const PINE = C(['#46735a', '#355c48', '#284838', '#1b3429', '#10211a']);
const CYPRESS = C(['#4b7442', '#3a5e36', '#2c4a2b', '#1f371f', '#122213']);
const CAMPHOR = C(['#65843d', '#4f6b30', '#3c5525', '#2a3e1b', '#18270f']);

function trunk(buf: PixelBuf, x: number, yBase: number, height: number, half: number): void {
  for (let y = 0; y < height; y++) {
    for (let dx = -half; dx <= half; dx++) {
      const k = dx < 0 ? 1 : dx === half ? 3 : 2;
      buf.set(x + dx, yBase - y, TRUNK[y % 7 === 3 && dx === 0 ? 3 : k]);
    }
  }
}

/**
 * 사천 소나무 — 산수화의 소나무처럼 굽은 줄기에서 가지가 좌우로 번갈아 뻗고,
 * 가지 끝마다 납작한 잎판이 얹힌다. 좌우가 어긋나야 사람 손으로 심은 것처럼 안 보인다.
 */
export function pine(seed: number): Prop {
  const r = rand(9300 + seed * 53);
  const w = 96;
  const h = 122;
  const buf = new PixelBuf(w, h);
  const base = h - 5;
  const cx = w / 2;
  shadow(buf, cx, base, 30, 6);
  // 살짝 굽은 줄기
  const lean = (r() - 0.5) * 0.35;
  const H = 92;
  const trunkX = (y: number): number => cx + lean * y + Math.sin(y / 22) * 2.5;
  for (let y = 0; y < H; y++) {
    const x = Math.round(trunkX(y));
    const half = y < 60 ? 2 : 1;
    for (let dx = -half; dx <= half; dx++) buf.set(x + dx, base - y, TRUNK[dx < 0 ? 1 : dx === half ? 3 : 2]);
  }
  // 잎판: 아래부터 위로, 좌우 번갈아
  const pads = 4 + Math.floor(r() * 2);
  let side = r() < 0.5 ? -1 : 1;
  const drawn: Array<() => void> = [];
  for (let i = 0; i < pads; i++) {
    const top = i === pads - 1;
    const u = i / (pads - 1);
    const y0 = base - 38 - u * (H - 42) + (r() - 0.5) * 6;
    const sx = trunkX(base - y0);
    const reach = top ? 0 : (10 + r() * 16) * (1 - u * 0.4);
    const px = sx + side * reach;
    const half = top ? 11 + r() * 5 : 12 + r() * 12 * (1 - u * 0.5);
    // 가지
    for (let j = 0; j <= Math.abs(px - sx); j++) {
      buf.set(Math.round(sx + side * j), Math.round(y0 + 3 - j * 0.25), TRUNK[2]);
    }
    const n = 3 + Math.floor(half / 5);
    // 잎판은 가지를 다 그린 뒤 얹는다(가지가 잎을 뚫고 나오지 않게)
    drawn.push(() => {
      for (let k = 0; k < n; k++) {
        const x = px + (k / Math.max(1, n - 1) - 0.5) * 2 * half + (r() - 0.5) * 3;
        const y = y0 + Math.pow(Math.abs(k / Math.max(1, n - 1) - 0.5) * 2, 2) * 4 + (r() - 0.5) * 3;
        clump(buf, x, y, 5 + r() * 2.5, PINE, r, 0.25 - u * 0.2);
      }
    });
    side = -side;
  }
  for (const f of drawn) f();
  return prop(buf);
}

/** 측백나무 — 사천 산길에 흔한 곧은 기둥 모양 */
export function cypress(seed: number): Prop {
  const r = rand(9500 + seed * 37);
  const w = 46;
  const h = 122;
  const buf = new PixelBuf(w, h);
  const base = h - 4;
  const cx = w / 2;
  shadow(buf, cx, base, 16, 4);
  trunk(buf, cx, base, 14, 1);
  const top = base - 110;
  // 아래부터 촘촘히 쌓는다 — 위에 찍은 덩이가 아래 덩이를 덮어 빛이 위에서 오는 것처럼 보인다
  const n = 46;
  for (let i = 0; i < n; i++) {
    const u = i / (n - 1); // 0 = 아래, 1 = 꼭대기
    const y = base - 14 - u * (base - 14 - top) + (r() - 0.5) * 4;
    // 아래는 좁게 시작해 가운데가 가장 넓고 끝은 뾰족하다
    const half = 13 * Math.pow(Math.sin(Math.PI * Math.min(1, 0.12 + u * 0.9)), 0.7);
    const x = cx + (r() - 0.5) * 2 * half * 0.55;
    clump(buf, x, y, 4 + r() * 2 + half * 0.18, CYPRESS, r, u * 0.4 - 0.1);
  }
  return prop(buf);
}

/** 녹나무 — 둥글고 넓게 퍼진 활엽수 */
export function camphor(seed: number): Prop {
  const r = rand(9700 + seed * 29);
  const w = 96;
  const h = 104;
  const buf = new PixelBuf(w, h);
  const base = h - 5;
  const cx = w / 2;
  shadow(buf, cx, base, 34, 7);
  trunk(buf, cx, base, 40, 3);
  // 줄기에서 가지가 갈라지는 자리
  for (let j = 0; j < 10; j++) {
    buf.set(cx - 4 - Math.round(j * 0.8), base - 36 - j, TRUNK[2]);
    buf.set(cx + 4 + Math.round(j * 0.7), base - 34 - j, TRUNK[3]);
  }
  const ccy = base - 62;
  for (let i = 0; i < 30; i++) {
    const a = r() * Math.PI * 2;
    const d = Math.sqrt(r());
    const x = cx + Math.cos(a) * d * 34;
    const y = ccy + Math.sin(a) * d * 24;
    clump(buf, x, y, 7 + r() * 4, CAMPHOR, r, (ccy - y) / 60);
  }
  return prop(buf);
}

// ── 덤불 · 바위 · 풀 ────────────────────────────────────

const TEA = C(['#5a8a3b', '#46702f', '#355824', '#243e19', '#14280e']);

/** 차나무 덤불 — 낮고 둥글다. 가끔 흰 꽃이 한두 점 */
export function bush(seed: number): Prop {
  const r = rand(9900 + seed * 43);
  const w = 46;
  const h = 30;
  const buf = new PixelBuf(w, h);
  const base = h - 3;
  shadow(buf, w / 2, base, 19, 3);
  for (let i = 0; i < 9; i++) {
    const x = 9 + r() * 28;
    const y = base - 7 - r() * 9 + Math.abs(x - w / 2) * 0.2;
    clump(buf, x, y, 5 + r() * 3, TEA, r);
  }
  const flower = hex('#b8b39a');
  if (r() < 0.6) for (let i = 0; i < 2; i++) buf.set(Math.floor(10 + r() * 26), Math.floor(base - 10 - r() * 8), flower);
  return prop(buf);
}

const ROCK = C(['#62666c', '#4e5157', '#3e4146', '#2f3135', '#1b1c1f']);
const MOSS = C(['#415b2d', '#324823', '#253719']);

/** 이끼 낀 바위. size = 반지름 */
export function rock(seed: number, size: number): Prop {
  const r = rand(10100 + seed * 67 + size);
  const rx = size;
  const ry = Math.round(size * 0.72);
  const w = rx * 2 + 6;
  const h = ry * 2 + 8;
  const buf = new PixelBuf(w, h);
  const cx = w / 2;
  const cy = h - ry - 4;
  shadow(buf, cx + 2, cy + ry * 0.7, rx + 2, Math.max(3, ry * 0.35));
  const N = 11;
  const rs: number[] = [];
  for (let i = 0; i < N; i++) rs.push(0.72 + r() * 0.28);
  const mossN = rand(10200 + seed);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x - cx) / rx;
      const dy = (y - cy) / ry;
      const d = Math.hypot(dx, dy);
      const a = (Math.atan2(dy, dx) + Math.PI) / (Math.PI * 2);
      const i0 = Math.floor(a * N) % N;
      const f = a * N - Math.floor(a * N);
      const rr = rs[i0] * (1 - f) + rs[(i0 + 1) % N] * f;
      if (d > rr) continue;
      if (d > rr - 1.4 / Math.min(rx, ry)) {
        buf.set(x, y, ROCK[4]);
        continue;
      }
      // 윗면이 밝고 아랫면이 어둡다. 윗면에 이끼
      const lit = -(dx * 0.45 + dy * 0.9);
      if (dy < -0.25 && mossN() < 0.55 + (-dy - 0.25)) {
        buf.set(x, y, MOSS[Math.max(0, Math.min(2, Math.round(1 - lit + (bayer(x, y) - 0.5))))]);
        continue;
      }
      const k = 2 - lit * 1.8 + (bayer(x, y) - 0.5) * 0.8;
      buf.set(x, y, ROCK[Math.max(0, Math.min(3, Math.round(k)))]);
    }
  }
  return prop(buf);
}

const GRASS = C(['#4a6632', '#3a5227', '#2c3f1d', '#1e2b14']);

/** 풀 한 포기 */
export function grass(seed: number): Prop {
  const r = rand(10300 + seed * 17);
  const w = 20;
  const h = 14;
  const buf = new PixelBuf(w, h);
  const base = h - 1;
  const n = 5 + Math.floor(r() * 5);
  for (let i = 0; i < n; i++) {
    const x0 = 4 + r() * 12;
    const len = 4 + Math.floor(r() * 8);
    const bend = (r() - 0.5) * 0.9;
    for (let j = 0; j < len; j++) {
      const x = Math.round(x0 + bend * j * (j / len));
      const c = j < 2 ? 3 : j > len - 3 ? (r() < 0.4 ? 0 : 1) : 2;
      buf.set(x, base - j, GRASS[c]);
    }
  }
  // 드물게 수수한 들꽃 한 점
  if (r() < 0.3) buf.set(Math.floor(5 + r() * 10), Math.floor(base - 6 - r() * 4), hex(r() < 0.5 ? '#7e6a8c' : '#8c7f52'));
  return prop(buf);
}

/** 잔돌 몇 개 */
export function pebbles(seed: number): Prop {
  const r = rand(10500 + seed * 13);
  const buf = new PixelBuf(26, 14);
  const n = 3 + Math.floor(r() * 4);
  for (let i = 0; i < n; i++) {
    const x = 2 + Math.floor(r() * 21);
    const y = 3 + Math.floor(r() * 8);
    const s = r() < 0.3 ? 2 : 1;
    for (let yy = 0; yy <= s; yy++) {
      for (let xx = 0; xx <= s; xx++) buf.set(x + xx, y + yy, ROCK[yy === 0 ? 1 : 3]);
    }
    buf.set(x + s + 1, y + s, ROCK[4]);
  }
  return prop(buf, 0.5, 0.5);
}

// ── 바닥에 눕는 것들 (흙·이끼 자국, 웅덩이, 낙엽, 안개) ────

/** 가장자리가 도트로 흩어지는 얼룩 하나 */
function patch(seed: number, w: number, h: number, ramp: RGB[]): Prop {
  const buf = new PixelBuf(w, h);
  const r = rand(seed);
  const cx = w / 2;
  const cy = h / 2;
  const N = 9;
  const rs: number[] = [];
  for (let i = 0; i < N; i++) rs.push(0.62 + r() * 0.38);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x - cx) / (w / 2);
      const dy = (y - cy) / (h / 2);
      const d = Math.hypot(dx, dy);
      const a = (Math.atan2(dy, dx) + Math.PI) / (Math.PI * 2);
      const i0 = Math.floor(a * N) % N;
      const f = a * N - Math.floor(a * N);
      const rr = rs[i0] * (1 - f) + rs[(i0 + 1) % N] * f;
      const edge = d / rr;
      if (edge > 1) continue;
      // 가장자리로 갈수록 성기게 — 바닥과 도트로 섞인다
      if (edge > 0.6 && bayer(x, y) < (edge - 0.6) / 0.4) continue;
      const k = Math.min(ramp.length - 1, Math.floor((1 - edge) * ramp.length * 0.9 + (bayer(x + 2, y) - 0.5)));
      buf.set(x, y, ramp[Math.max(0, k)]);
    }
  }
  return prop(buf, 0.5, 0.5);
}

/** 붉은 흙이 드러난 자리 */
export function soilPatch(seed: number): Prop {
  return patch(11000 + seed * 31, 100 + seed * 13, 64 + seed * 7, C(['#1d140e', '#22170f', '#281b12', '#2e2015']));
}

/** 이끼가 짙은 자리 */
export function mossPatch(seed: number): Prop {
  return patch(11500 + seed * 29, 84 + seed * 11, 56 + seed * 5, C(['#151e0f', '#192412', '#1d2a15', '#213018']));
}

/** 빗물 웅덩이 — 어둡게 비치고 한 줄 빛이 선다 */
export function puddle(seed: number): Prop {
  const r = rand(12000 + seed * 19);
  const w = 44 + seed * 8;
  const h = 18 + seed * 3;
  const buf = new PixelBuf(w, h);
  const cx = w / 2;
  const cy = h / 2;
  const deep = C(['#0b0f14', '#10161e', '#151e29', '#243446']);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const e = ((x - cx) / (w / 2 - 1)) ** 2 + ((y - cy) / (h / 2 - 1)) ** 2;
      if (e > 1 + (r() - 0.5) * 0.12) continue;
      buf.set(x, y, deep[e > 0.8 ? 0 : e > 0.35 ? 1 : 2]);
    }
  }
  // 하늘이 비치는 줄
  const y0 = Math.round(cy - 1);
  for (let x = Math.round(cx - w * 0.22); x < cx + w * 0.12; x++) if (r() < 0.8) buf.set(x, y0, deep[3]);
  for (let x = Math.round(cx - w * 0.05); x < cx + w * 0.2; x++) if (r() < 0.5) buf.set(x, y0 + 2, deep[2]);
  return prop(buf, 0.5, 0.5);
}

/** 떨어진 잎 — 붉고 누런 점이 흩어져 있다 */
export function leaves(seed: number): Prop {
  const r = rand(12500 + seed * 23);
  const w = 56;
  const h = 36;
  const buf = new PixelBuf(w, h);
  const cols = C(['#4a331b', '#5a2a19', '#5e5025', '#3c2e1a', '#4b3a1d']);
  const n = 14 + Math.floor(r() * 14);
  for (let i = 0; i < n; i++) {
    const a = r() * Math.PI * 2;
    const d = Math.sqrt(r());
    const x = Math.round(w / 2 + Math.cos(a) * d * (w / 2 - 2));
    const y = Math.round(h / 2 + Math.sin(a) * d * (h / 2 - 2));
    const c = cols[Math.floor(r() * cols.length)];
    buf.set(x, y, c);
    if (r() < 0.5) buf.set(x + 1, y, c);
  }
  return prop(buf, 0.5, 0.5);
}

/**
 * 보물 상자 — 엘리트가 떨군다. 바닥 장식과 헷갈리면 안 되므로 맵에서 가장 밝은 금빛에 흰 테를 두른다.
 * 앞면 가운데 자물쇠, 뚜껑은 둥글게.
 */
export function chestTexture(): Texture {
  const w = 30;
  const h = 26;
  const buf = new PixelBuf(w, h);
  const wood = C(['#e0a050', '#b8742e', '#7a4518', '#3e1f08']);
  const gold = C(['#ffffff', '#fff3b0', '#ffd23a', '#b98a10']);
  for (let y = 2; y < h - 1; y++) {
    for (let x = 2; x < w - 2; x++) {
      const lid = y < 11;
      // 뚜껑 모서리를 둥글게
      if (lid && (x - 2 < 3 - (y - 2) * 0.6 || w - 3 - x < 3 - (y - 2) * 0.6)) continue;
      const edge = x === 2 || x === w - 3 || y === 2 || y === h - 2;
      const band = y === 10 || y === 11 || x === 7 || x === w - 8;
      const c = edge ? gold[3] : band ? gold[2] : wood[lid ? (y < 5 ? 0 : 1) : y > h - 5 ? 2 : 1];
      buf.set(x, y, c);
    }
  }
  // 자물쇠
  for (let y = 10; y <= 15; y++) for (let x = 13; x <= 16; x++) buf.set(x, y, gold[y === 10 ? 0 : 1]);
  buf.set(14, 13, wood[3]);
  buf.set(15, 13, wood[3]);
  // 반짝임
  buf.set(5, 4, gold[0]);
  buf.set(6, 4, gold[0]);
  buf.set(5, 5, gold[0]);
  return buf.toTexture();
}

/** 옅은 안개 — 가운데가 짙고 가장자리로 도트가 성겨진다. 스프라이트 알파를 낮게 걸어 쓴다 */
export function mist(seed: number): Prop {
  const w = 240;
  const h = 110;
  const buf = new PixelBuf(w, h);
  const r = rand(13000 + seed * 41);
  const c = hex('#aab6ba');
  const blobs: Array<[number, number, number]> = [];
  for (let i = 0; i < 6; i++) blobs.push([40 + r() * 160, 35 + r() * 40, 30 + r() * 30]);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let v = 0;
      for (const [bx, by, br] of blobs) v = Math.max(v, 1 - Math.hypot(x - bx, (y - by) * 1.6) / br);
      if (v <= 0) continue;
      if (bayer(x, y) < v * 0.9) buf.set(x, y, c);
    }
  }
  return prop(buf, 0.5, 0.5);
}
